import { List } from 'immutable';
import { createSelector } from 'reselect';
import TwoFactorAuthStepOrder from '../constants/TwoFactorAuthStepOrder';
import * as TwoFactorAuthSetupSteps from '../constants/TwoFactorAuthSetupSteps';
import * as TwoFactorAuthDeviceStatuses from '../constants/TwoFactorAuthDeviceStatuses';
import { getTwoFactorConfirmedRequestStatus } from '../selectors/RequestStatusSelector';
import RequestStatusTypes from '../constants/RequestStatusTypes';
import * as TwoFactorAuthMethods from '../constants/TwoFactorAuthMethods';
import TwoFactorAuthSMSErrors from '../constants/TwoFactorAuthSMSErrors';
import * as TwoFactorAuthPrecedenceTypes from '../constants/TwoFactorAuthPrecedenceTypes';
const getPersistedStatus = state => state.twoFactorAuth.persistedStatus;
export const getSetupStep = state => {
  const {
    setupStepIndex
  } = state.twoFactorAuth;
  if (setupStepIndex === -1) {
    return TwoFactorAuthSetupSteps.UNINITIALIZED;
  }
  return TwoFactorAuthStepOrder[state.twoFactorAuth.setupStepIndex];
};
const getBackupCodesResponse = createSelector([getPersistedStatus], status => status.get('backupCodesResponse'));
export const getBackupCodes = createSelector([getBackupCodesResponse], response => {
  return response ? response.get('backupCodes') : List();
});
export const getBackupCodesLoading = state => state.twoFactorAuth.backupCodesLoading;
export const getRemainingCodes = createSelector([getBackupCodes], codes => {
  const usedCodes = codes.filter(backupCode => backupCode.get('usedAt') !== null).size;
  return codes.size - usedCodes;
});
export const getBackupCodesCreatedAt = createSelector([getBackupCodesResponse], response => {
  return response ? response.get('createdAt') : null;
});
export const getBackupModalVisible = state => state.twoFactorAuth.backupModalVisible;
export const getStagedMethod = state => state.twoFactorAuth.method;
export const getMethodPrecedence = state => state.twoFactorAuth.precedence;
export const getQRCode = state => state.twoFactorAuth.qrCode;
export const getVerificationCodeAttempt = state => state.twoFactorAuth.verificationCodeAttempt;
export const getVerificationCodeAttemptFailed = state => state.twoFactorAuth.verificationCodeFailure;
export const getStagedSMSNumber = state => state.twoFactorAuth.stagedSMSNumber;
const twoFactorConfirmedRequestStatusIsPending = createSelector([getTwoFactorConfirmedRequestStatus], requestStatus => {
  return requestStatus === RequestStatusTypes.PENDING;
});
const userPrintedOrDownloadedBackupCodes = state => state.twoFactorAuth.printedOrSavedBackupCodes;
const getPrimaryMethodStatus = createSelector([getPersistedStatus], persistedStatus => persistedStatus.getIn(['primary', 'status']));
const getSecondaryMethodStatus = createSelector([getPersistedStatus], persistedStatus => persistedStatus.getIn(['secondary', 'status']));
export const getPrimaryAuthMethod = createSelector([getPersistedStatus, getPrimaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['primary', 'method']) : null;
});
export const getSecondaryAuthMethod = createSelector([getPersistedStatus, getSecondaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['secondary', 'method']) : null;
});
export const getShouldSetupSecondaryAuthMethod = createSelector([getSetupStep, getMethodPrecedence, getSecondaryMethodStatus], (step, precedence, secondaryMethodStatus) => {
  return step === TwoFactorAuthSetupSteps.VERIFICATION_SUCCESSFUL && precedence === TwoFactorAuthPrecedenceTypes.PRIMARY && secondaryMethodStatus !== TwoFactorAuthDeviceStatuses.CONFIRMED;
});
export const getSetupForLoginAccountInfo = state => state.twoFactorAuth.setupForLoginAccountInfo;
export const getSetupForLoginPortalId = createSelector([getSetupForLoginAccountInfo], accountInfo => accountInfo.get('portalId'));
export const isSetupForLogin = state => state.twoFactorAuth.setupForLogin;
const getPrimaryInfo = createSelector([getPersistedStatus, getPrimaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['primary', 'info']) : null;
});
const getSecondaryInfo = createSelector([getPersistedStatus, getSecondaryMethodStatus], (persistedStatus, status) => {
  return status === TwoFactorAuthDeviceStatuses.CONFIRMED ? persistedStatus.getIn(['secondary', 'info']) : null;
});
const getConfiguredSMSNumbers = createSelector([getPrimaryAuthMethod, getPrimaryInfo, getSecondaryAuthMethod, getSecondaryInfo], (primaryMethod, primaryInfo, secondaryMethod, secondaryInfo) => {
  const used = [];
  if (primaryMethod === TwoFactorAuthMethods.SMS) {
    used.push(primaryInfo);
  }
  if (secondaryMethod === TwoFactorAuthMethods.SMS) {
    used.push(secondaryInfo);
  }
  return used;
});
const isErrorSendingSMS = state => state.twoFactorAuth.isErrorSendingSMS;
export const getSMSRegisterError = createSelector([isErrorSendingSMS, getConfiguredSMSNumbers, getStagedSMSNumber], (errorSendingSMS, usedNumbers, stagedSMSNumber) => {
  if (errorSendingSMS !== undefined && errorSendingSMS) {
    return TwoFactorAuthSMSErrors.SMS_NOT_SENT;
  }
  if (usedNumbers.indexOf(stagedSMSNumber) >= 0) {
    return TwoFactorAuthSMSErrors.NUMBER_ALREADY_REGISTERED;
  }
  return undefined;
});
export const getShouldBlockSetupProgress = createSelector([getVerificationCodeAttempt, twoFactorConfirmedRequestStatusIsPending, getSetupStep, getSMSRegisterError, userPrintedOrDownloadedBackupCodes], (verificationCodeAttempt, isPending, setupStep, smsRegisterError, printedOrSavedBackupCodes) => {
  if (setupStep === TwoFactorAuthSetupSteps.VERIFY_GOOGLE_AUTHENTICATOR || setupStep === TwoFactorAuthSetupSteps.VERIFY_SMS) {
    return verificationCodeAttempt === '';
  }
  if (setupStep === TwoFactorAuthSetupSteps.REGISTER_SMS) {
    return smsRegisterError === TwoFactorAuthSMSErrors.NUMBER_ALREADY_REGISTERED;
  }
  if (setupStep === TwoFactorAuthSetupSteps.BACKUP_CODES) {
    return !printedOrSavedBackupCodes;
  }
  return isPending;
});
export const getShouldRenderGoogleAuthWarning = createSelector([getPrimaryAuthMethod, getStagedMethod], (primaryAuthMethod, stagedMethod) => {
  return stagedMethod === TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR && primaryAuthMethod === TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR;
});
export const getShouldStagedMethodBeGoogleAuth = createSelector([getPrimaryAuthMethod], primaryAuthMethod => {
  return primaryAuthMethod !== TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR;
});
export const getShouldNotSetupOnLogin = createSelector([isSetupForLogin, getPrimaryAuthMethod, getSetupStep], (setupForLogin, primaryAuthMethod, setupStep) => setupForLogin && primaryAuthMethod !== null && setupStep === TwoFactorAuthSetupSteps.CHOOSE_METHOD);
export const getUserLoginRequired = state => state.twoFactorAuth.requiresUserToLogin;